import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ReceiptText,
  Printer,
  Ban,
  Wallet,
  CheckCircle2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Badge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { Loader, EmptyState } from '@/components/common/Loader'
import { billingApi } from '@/api/billingApi'
import { patientApi } from '@/api/patientApi'
import { billPaymentUpdateSchema } from '@/lib/validators'
import { CLINIC, DRUG_CATEGORY_LABEL } from '@/lib/constants'
import { cn, formatDate, formatMobile, formatRupees } from '@/lib/utils'

export default function BillDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [bill, setBill] = useState(null)
  const [patient, setPatient] = useState(null)
  const [loading, setLoading] = useState(true)

  const [payOpen, setPayOpen] = useState(false)
  const [payAmount, setPayAmount] = useState('')
  const [payError, setPayError] = useState('')
  const [paying, setPaying] = useState(false)

  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  const loadBill = async () => {
    setLoading(true)
    try {
      const b = await billingApi.get(id)
      setBill(b)
      patientApi
        .get(b.patient_id)
        .then((p) => setPatient(p))
        .catch(() => {})
    } catch {
      toast.error('Could not load bill')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBill()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const openPay = () => {
    setPayAmount(String(bill?.paid ?? ''))
    setPayError('')
    setPayOpen(true)
  }

  const handlePay = async () => {
    const parsed = billPaymentUpdateSchema.safeParse({ paid: payAmount })
    if (!parsed.success) {
      setPayError(parsed.error.issues[0]?.message || 'Invalid amount')
      return
    }
    if (parsed.data.paid > Number(bill.total)) {
      setPayError(`Paid cannot exceed total (${formatRupees(bill.total)})`)
      return
    }
    setPaying(true)
    try {
      const updated = await billingApi.updatePayment(id, parsed.data)
      setBill(updated)
      toast.success('Payment updated')
      setPayOpen(false)
    } catch {
      // toast handled
    } finally {
      setPaying(false)
    }
  }

  const handleCancel = async () => {
    setCancelling(true)
    try {
      const updated = await billingApi.cancel(id)
      setBill(updated)
      toast.success('Bill cancelled — stock restored')
      setCancelOpen(false)
    } catch {
      // toast handled
    } finally {
      setCancelling(false)
    }
  }

  if (loading) {
    return (
      <div className="container-page py-6 sm:py-8">
        <Loader label="Loading bill…" />
      </div>
    )
  }

  if (!bill) {
    return (
      <div className="container-page py-6 sm:py-8">
        <EmptyState
          icon={<ReceiptText className="h-6 w-6" />}
          title="Bill not found"
          description="The bill ID may be wrong or the bill was deleted."
          action={
            <Button onClick={() => navigate('/billing')} leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Back to bills
            </Button>
          }
        />
      </div>
    )
  }

  const isCancelled = bill.status === 'cancelled'
  const isPaid = !isCancelled && Number(bill.balance) <= 0

  return (
    <div className="container-page py-6 sm:py-8">
      <PageHeader
        eyebrow="Phase 7 — Billing"
        title={
          <span className="flex items-center gap-3">
            <ReceiptText className="h-6 w-6 text-primary-700" />
            <span>Bill</span>
            <span className="font-mono text-lg text-ink-500">{bill.bill_id}</span>
          </span>
        }
        description={`Created on ${formatDate(bill.created_at, { withTime: true })}`}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate('/billing')}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back
            </Button>
            <Button
              variant="ghost"
              onClick={() => window.print()}
              leftIcon={<Printer className="h-4 w-4" />}
              className="print:hidden"
            >
              Print
            </Button>
            {!isCancelled && (
              <Button
                variant="ghost"
                onClick={() => setCancelOpen(true)}
                leftIcon={<Ban className="h-4 w-4" />}
                className="text-rose-600 hover:bg-rose-50 print:hidden"
              >
                Cancel bill
              </Button>
            )}
            {!isCancelled && !isPaid && (
              <Button onClick={openPay} leftIcon={<Wallet className="h-4 w-4" />} className="print:hidden">
                Update payment
              </Button>
            )}
          </>
        }
      />

      {/* Status banner */}
      {isCancelled && (
        <div className="mt-4 rounded-2xl bg-rose-50 p-3 text-sm font-semibold text-rose-800 ring-1 ring-rose-200">
          This bill is cancelled. Stock has been restored to inventory.
          {bill.cancelled_at && (
            <span className="ml-2 font-normal">
              · Cancelled {formatDate(bill.cancelled_at, { withTime: true })}
            </span>
          )}
        </div>
      )}
      {isPaid && (
        <div className="mt-4 flex items-center gap-2 rounded-2xl bg-emerald-50 p-3 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-200">
          <CheckCircle2 className="h-4 w-4" /> Fully paid.
        </div>
      )}

      <div className="mt-6 rounded-2xl bg-white p-6 shadow-soft ring-1 ring-ink-100 sm:p-8 print:shadow-none print:ring-0">
        {/* Clinic header */}
        <div className="flex flex-col gap-4 border-b border-ink-100 pb-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="h-display text-xl font-bold text-primary-800 sm:text-2xl">
              {CLINIC.name}
            </p>
            <p className="mt-0.5 text-xs text-ink-500">{CLINIC.tagline}</p>
            <p className="mt-2 max-w-sm text-xs text-ink-600">{CLINIC.address}</p>
            <p className="mt-0.5 text-xs text-ink-600">
              {CLINIC.phone} · {CLINIC.email}
            </p>
            {CLINIC.gstin && (
              <p className="mt-0.5 text-[11px] font-mono text-ink-500">GSTIN {CLINIC.gstin}</p>
            )}
          </div>
          <div className="text-left sm:text-right">
            <p className="label-text">Bill</p>
            <p className="font-mono text-base text-ink-900">{bill.bill_id}</p>
            <p className="mt-2 label-text">Date</p>
            <p className="text-sm text-ink-900">{formatDate(bill.bill_date)}</p>
          </div>
        </div>

        {/* Patient block */}
        <div className="grid gap-4 border-b border-ink-100 py-5 sm:grid-cols-2">
          <div>
            <p className="label-text">Billed to</p>
            {patient ? (
              <>
                <Link
                  to={`/patients/${patient.patient_id}`}
                  className="text-base font-semibold text-primary-800 hover:underline print:text-ink-900"
                >
                  {patient.name}
                </Link>
                <p className="text-xs text-ink-500">
                  <span className="font-mono">{patient.patient_id}</span> ·{' '}
                  {formatMobile(patient.mobile_number)}
                </p>
                {patient.address && (
                  <p className="mt-1 text-xs text-ink-600">{patient.address}</p>
                )}
              </>
            ) : (
              <p className="font-mono text-sm text-ink-700">{bill.patient_id}</p>
            )}
          </div>
          <div className="sm:text-right">
            {bill.treatment_plan && (
              <>
                <p className="label-text">Treatment plan</p>
                <p className="text-sm text-ink-800">{bill.treatment_plan}</p>
              </>
            )}
            {bill.visit_id && (
              <p className="mt-1 text-[11px] font-mono text-ink-500">
                Linked visit · {bill.visit_id}
              </p>
            )}
          </div>
        </div>

        {/* Items table */}
        {bill.items?.length > 0 ? (
          <div className="-mx-6 overflow-x-auto py-5 sm:-mx-8">
            <table className="w-full text-sm">
              <thead className="text-left">
                <tr className="border-b border-ink-100">
                  <th className="px-6 py-2 label-text sm:px-8">Medicine</th>
                  <th className="px-6 py-2 label-text">Category</th>
                  <th className="px-6 py-2 label-text text-right">Unit price</th>
                  <th className="px-6 py-2 label-text text-right">Qty</th>
                  <th className="px-6 py-2 label-text text-right sm:px-8">Line total</th>
                </tr>
              </thead>
              <tbody>
                {bill.items.map((it) => (
                  <tr key={it.id} className="border-b border-ink-100/60">
                    <td className="px-6 py-3 sm:px-8">
                      <p className="font-semibold text-ink-900">{it.drug_name}</p>
                      <p className="text-[11px] font-mono text-ink-500">{it.drug_id}</p>
                    </td>
                    <td className="px-6 py-3 text-ink-700">
                      {DRUG_CATEGORY_LABEL[it.category_id] || it.category_id}
                    </td>
                    <td className="px-6 py-3 text-right tabular-nums text-ink-700">
                      {formatRupees(it.unit_price)}
                    </td>
                    <td className="px-6 py-3 text-right tabular-nums text-ink-700">
                      {it.quantity}
                    </td>
                    <td className="px-6 py-3 text-right tabular-nums font-semibold text-ink-900 sm:px-8">
                      {formatRupees(it.line_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border-b border-ink-100 py-5 text-center text-sm text-ink-500">
            No medicines on this bill (service-only).
          </div>
        )}

        {/* Totals */}
        <div className="mt-5 ml-auto max-w-sm space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-ink-500">Service charge</span>
            <span className="tabular-nums text-ink-800">
              {formatRupees(bill.service_charge)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-500">Medicine charge</span>
            <span className="tabular-nums text-ink-800">
              {formatRupees(bill.medicine_charge)}
            </span>
          </div>
          <div className="my-1 border-t border-ink-100" />
          <div className="flex justify-between text-base">
            <span className="font-semibold text-ink-900">Total</span>
            <span className="font-bold tabular-nums text-ink-900">
              {formatRupees(bill.total)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-500">Paid</span>
            <span className="tabular-nums text-emerald-700">{formatRupees(bill.paid)}</span>
          </div>
          <div
            className={cn(
              'flex justify-between rounded-xl px-3 py-2',
              Number(bill.balance) > 0 ? 'bg-accent-50' : 'bg-emerald-50',
            )}
          >
            <span className="font-semibold text-ink-900">Balance</span>
            <span
              className={cn(
                'font-bold tabular-nums',
                Number(bill.balance) > 0 ? 'text-accent-700' : 'text-emerald-700',
              )}
            >
              {formatRupees(bill.balance)}
            </span>
          </div>
        </div>

        {bill.notes && (
          <div className="mt-6 border-t border-ink-100 pt-4">
            <p className="label-text">Notes</p>
            <p className="mt-1 text-sm text-ink-700">{bill.notes}</p>
          </div>
        )}

        <p className="mt-6 border-t border-ink-100 pt-4 text-center text-[11px] text-ink-500">
          Thank you for choosing {CLINIC.name}. Get well soon!
        </p>
      </div>

      {/* Payment modal */}
      <Modal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        title="Update payment"
        description="Set the total paid amount. Balance recomputes automatically."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setPayOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePay} loading={paying}>
              Save payment
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-xl bg-cream/60 p-3 text-sm ring-1 ring-ink-100">
            <div className="flex justify-between">
              <span className="text-ink-500">Bill total</span>
              <span className="font-semibold tabular-nums text-ink-900">
                {formatRupees(bill.total)}
              </span>
            </div>
          </div>
          <Input
            label="Total paid so far (₹)"
            required
            type="number"
            min="0"
            step="0.01"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            error={payError}
            hint={`Currently ${formatRupees(bill.paid)}. New balance is auto-calculated.`}
          />
        </div>
      </Modal>

      {/* Cancel modal */}
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel this bill?"
        description="Stock for every dispensed medicine will be restored back to the inventory. This is recorded in the audit trail and cannot be undone."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Keep bill
            </Button>
            <Button variant="danger" onClick={handleCancel} loading={cancelling}>
              Cancel & restore stock
            </Button>
          </>
        }
      />
    </div>
  )
}
